import { InvalidateQueryFilters, QueryClient } from '@tanstack/react-query'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'
import zod from 'zod'

import { viewQueryInvalidationMap as optimusViewQueryInvalidationMap } from '~/clients/optimus-view-queries.generated'
import { viewQueryInvalidationMap as coreViewQueryInvalidationMap } from '~/clients/view-queries.generated'
import { useStore } from '~/store/store'
import { cluster } from '~/utils/array'

import { getEntityTypeImportMap, RequiredEntityType } from './entityTypeImportMap'

const viewQueryInvalidationMap: Record<string, string[]> = {
    ...coreViewQueryInvalidationMap,
    ...optimusViewQueryInvalidationMap,
}

export const cachedQueriesKeys = {
    blockScheduleDuration: 'blockScheduleDuration',
    blockScheduleUtilization: 'blockScheduleUtilization',
    blockScheduleOvertime: 'blockScheduleOvertime',
    blockScheduleEstimation: 'blockScheduleEstimation',
    staffingPlanEvaluation: 'staffingPlanEvaluation',
    surgeryProgramStatus: 'surgeryProgramStatus',
    surgeonWaitingListPrediction: 'surgeonWaitingListPrediction',
    getEvaluations: 'getEvaluations',
    getWardOccupancies: 'getWardOccupancies',
    unreadInboxEntriesCount: 'unreadInboxEntriesCount',
    postOpConstraintCandidates: 'postOpConstraintCandidates',
    postOpConstraintCandidateGroup: 'postOpConstraintCandidateGroup',
    postOpConstraintsForAvailableTimes: 'postOpConstraintsForAvailableTimes',
    equipmentConstraintCandidates: 'equipmentConstraintCandidates',
    equipmentConstraintCandidateGroup: 'equipmentConstraintCandidateGroup',
    equipmentConstraintsForAvailableTimes: 'equipmentConstraintsForAvailableTimes',
    staffingSurgeryGroupsValidation: 'staffingSurgeryGroupsValidation',
    surgeryDurations: 'surgeryDurations',
}

type CachedQuery = {
    entityTypes: RequiredEntityType[]
    invalidateOptions?: Omit<InvalidateQueryFilters, 'queryKey'>
}
// keep mapping of which queries are dependent on which entities
export const cachedQueries: Record<string, CachedQuery> = {
    [cachedQueriesKeys.blockScheduleUtilization]: {
        entityTypes: ['block_schedule', 'practitioner_schedule', 'planning_period', 'location', 'practitioner_schedule_location'],
    },
    [cachedQueriesKeys.blockScheduleDuration]: {
        entityTypes: ['block_schedule', 'practitioner_schedule', 'planning_period', 'location', 'practitioner_schedule_location'],
    },
    [cachedQueriesKeys.blockScheduleOvertime]: {
        entityTypes: ['block_schedule', 'practitioner_schedule', 'planning_period', 'location', 'practitioner_schedule_location'],
    },
    [cachedQueriesKeys.blockScheduleEstimation]: {
        entityTypes: ['block_schedule', 'practitioner_schedule', 'planning_period', 'location', 'practitioner_schedule_location', 'location_service_override'],
    },
    [cachedQueriesKeys.staffingPlanEvaluation]: {
        entityTypes: ['practitioner_schedule', 'practitioner_schedule_location', 'practitioner_schedule_status'],
    },
    [cachedQueriesKeys.getEvaluations]: {
        entityTypes: [
            'block_schedule',
            'surgery_type_group',
            'hospital_surgery_type',
            'surgery_type_group_hierarchy',
            'hospital_surgery_type_group_association',
            'age_group',
        ],
    },
    [cachedQueriesKeys.getWardOccupancies]: {
        entityTypes: ['block_schedule'],
    },
    [cachedQueriesKeys.unreadInboxEntriesCount]: {
        entityTypes: ['inbox_entry'],
    },
    [cachedQueriesKeys.equipmentConstraintCandidates]: {
        entityTypes: ['location', 'surgery_type_group', 'surgery_type_group_hierarchy', 'hospital_surgery_type_group_association'],
    },
    [cachedQueriesKeys.equipmentConstraintCandidateGroup]: {
        entityTypes: ['location', 'surgery_type_group', 'surgery_type_group_hierarchy', 'hospital_surgery_type_group_association'],
    },
    [cachedQueriesKeys.postOpConstraintCandidates]: {
        entityTypes: ['location', 'post_op', 'post_op_capacity_period', 'post_op_capacity_override', 'location_post_op_association', 'block_schedule'],
    },
    [cachedQueriesKeys.postOpConstraintCandidateGroup]: {
        entityTypes: ['location', 'post_op', 'post_op_capacity_period', 'post_op_capacity_override', 'location_post_op_association', 'block_schedule'],
    },
    [cachedQueriesKeys.surgeonWaitingListPrediction]: {
        entityTypes: ['practitioner_schedule_status', 'practitioner_schedule_location'],
        invalidateOptions: {
            // respect stale-time, such that we dont refetch on every change to a status
            // will only invalidate and refetch if stale
            stale: true,
        },
    },
    [cachedQueriesKeys.equipmentConstraintsForAvailableTimes]: {
        entityTypes: [
            'age_group',
            'block_schedule',
            'location',
            'surgery_type_group',
            'surgery_type_group_hierarchy',
            'hospital_surgery_type_group_association',
        ],
    },
    [cachedQueriesKeys.postOpConstraintsForAvailableTimes]: {
        entityTypes: [
            'age_group',
            'block_schedule',
            'location',
            'post_op',
            'post_op_capacity_period',
            'post_op_capacity_override',
            'location_post_op_association',
        ],
    },
    [cachedQueriesKeys.staffingSurgeryGroupsValidation]: {
        entityTypes: ['staffing_surgery_group', 'staffing_surgery_group_surgery_type_group'],
    },
    [cachedQueriesKeys.surgeryDurations]: {
        entityTypes: ['hospital_surgery_type', 'location', 'practitioner_schedule', 'practitioner_schedule_location'],
    },
}

export const WebSocketEventSchema = zod.object({
    entity_type: zod.string(),
    id: zod.number(),
    uuid: zod.string().optional(),
    event_type: zod.union([zod.literal('delete'), zod.literal('create'), zod.literal('update')]),
})
type WebSocketEvent = zod.infer<typeof WebSocketEventSchema>

const WebSocketEventsSchema = zod.array(WebSocketEventSchema)

function isEntityEventLike(object: unknown): object is { entity_type: string; id: number } {
    return typeof object === 'object' && object !== null && 'entity_type' in object && 'id' in object
}

function fetchAndApplyUpdatesToStore(events: WebSocketEvent[]) {
    const byEventType = groupBy(events, 'event_type')

    if (byEventType['delete'] && byEventType['delete'].length > 0) {
        useStore.getState().di.actions.removeEntitiesByWebsocketEvent(byEventType['delete'])
    }

    const entitiesToLoad = [...(byEventType['update'] ?? []), ...(byEventType['create'] ?? [])]

    if (entitiesToLoad.length > 0) {
        const entitiesByType = groupBy(entitiesToLoad, 'entity_type')
        const entityTypeImportMap = getEntityTypeImportMap()

        const promises = map(entitiesByType, (events, entityType) => {
            const ids = events.map(event => event.id)
            const importCall = entityTypeImportMap[entityType as keyof typeof entityTypeImportMap]

            // Suppressing the error here, as I am not sure whether types are correctly inferred.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!importCall) {
                // console.error('No import function found for entity type:', entityType)
                return Promise.resolve()
            }
            return importCall({ 'id:in': cluster(ids) })
        })

        Promise.all(promises).catch(error => {
            console.error('Failed to update entities:', error)
        })
    }
}

const hasIntersection = (a: string[], b: string[]) => a.some(value => b.includes(value))

export function addEvents(eventString: string, queryClient: QueryClient) {
    let event: unknown
    try {
        event = JSON.parse(eventString)
    } catch (error) {
        console.error('Expected JSON for WebSocket event, received:', error)
        return
    }

    // Ignore any events that are completely something else, like the PING/PONG messages
    const validEvents = (Array.isArray(event) ? event : [event]).filter(isEntityEventLike)

    // We do a more thorough runtime check of the event to ensure it matches our schema
    const result = WebSocketEventsSchema.safeParse(validEvents)

    if (!result.success) {
        console.error(`Invalid WebSocket event:`, event)
        return
    }

    // Find all queries that are affected by the event
    const keysToUpdate = Object.keys(cachedQueries).filter(key =>
        hasIntersection(
            cachedQueries[key]?.entityTypes ?? [],
            result.data.map(entityEvent => entityEvent.entity_type)
        )
    )

    keysToUpdate.forEach(key => {
        const invalidateOptions = cachedQueries[key]?.invalidateOptions
        void queryClient.invalidateQueries({
            queryKey: [key],
            ...invalidateOptions,
        })
    })

    fetchAndApplyUpdatesToStore(result.data)

    const changedEntityTypes = result.data.map(e => e.entity_type)

    for (const [queryKeyName, dependentEntityTypes] of Object.entries(viewQueryInvalidationMap)) {
        if (hasIntersection(dependentEntityTypes, changedEntityTypes)) {
            void queryClient.invalidateQueries({ queryKey: ['view', queryKeyName] })
        }
    }

    // Fallback: invalidate view queries not covered by the map.
    // This ensures view endpoints missing x-invalidated-by metadata still get
    // invalidated (matching current behavior), while mapped queries benefit from
    // targeted invalidation. Remove this fallback once all view endpoints have
    // x-invalidated-by annotations.
    void queryClient.invalidateQueries({
        queryKey: ['view'],
        predicate: query => {
            const viewName = query.queryKey[1]
            return typeof viewName === 'string' && !viewQueryInvalidationMap[viewName]
        },
    })
}
